import React from 'react';
import {useDispatch} from 'react-redux';
import {Location, WeatherWarningListResponse} from '../api/types';
import {pushLocationWeatherWarnings} from '../store/weatherData';
import {getLocationWeatherWarnings} from '../store/weatherData/selectors';
import useParamSelector from './useParamSelector';
import useWeatherWarningsSync, {
  WeatherWarningsOptions,
} from './useWeatherWarningsSync';

/**
 * Official weather warnings at a saved location, cached in the store per
 * location hash. Same contract as `useStationWarning`.
 */
const useLocationWarning = (
  location: Location,
  {autoRefresh = false}: WeatherWarningsOptions = {},
) => {
  const dispatch = useDispatch();

  const locationHash = location?.hash;

  const warningsData: WeatherWarningListResponse | null = useParamSelector(
    getLocationWeatherWarnings,
    locationHash,
  );

  const onData = React.useCallback(
    (warningListResponse: WeatherWarningListResponse) => {
      dispatch(
        pushLocationWeatherWarnings({locationHash, warningListResponse}),
      );
    },
    [dispatch, locationHash],
  );

  const {update, isUpdateRequired, isUpdating, hasError, computedAt} =
    useWeatherWarningsSync({
      storeKey: locationHash,
      lat: location?.coordinates?.latitude,
      lon: location?.coordinates?.longitude,
      data: warningsData,
      onData,
      autoRefresh,
    });

  const warnings = warningsData?.warnings;

  return React.useMemo(
    () => ({
      updateLocationWarnings: update,
      warnings: warnings || [],
      isWarningUpdateRequired: isUpdateRequired,
      isUpdating,
      /** The last fetch for this place failed (see `useWeatherWarningsSync`). */
      hasError,
      /** The stored response's `computed_at` (epoch seconds), else null. */
      computedAt,
    }),
    [update, warnings, isUpdateRequired, isUpdating, hasError, computedAt],
  );
};

export default useLocationWarning;
